import { bisector } from "d3-array";
import { interpolateNumber } from "d3-interpolate";

import { IDataSet, IDataSequence, SequenceState, ISample, TooltipItem, TooltipInfo } from "../../Data";
import { Store } from "../../../flux/Store";
import { Key } from "../../../models";
import { assertValue } from "../../../app/utils/assert/index";
import { getClip } from "../../../d3chart/zoom";

import { names } from "./TimeSeriesActions";
var _ = require('underscore');

type Numeric = { valueOf(): number; };
type Domain = number | Numeric;

export interface TimeSeriesState
{
    dataSets: IDataSet[];
    range: [Date, Date];
    zoomedDataSets: IDataSet[],
    tooltip: TooltipInfo,
}

// This is a storage which holds data needed to render TimeSeries chart properly
export class TimeSeriesStore extends Store<TimeSeriesState>
{
    constructor()
    {
        super();
        
        this.register<IDataSet[]>(names.SET_DATA_SETS, e => reduceStateFor(initialState(), e.payload, true));
        this.register<Key>(names.HIGHLIGHT_SINGLE, e => {
            const target = e.payload;

            let { dataSets } = this.state;

            const nextDataSets = dataSets.map(ds => ({
                ...ds,
                sequences: ds.sequences.map(seq => ({
                    ...seq,
                    state: (seq.id === target) ? SequenceState.Highlighted : ((seq.state === SequenceState.Hidden) ? SequenceState.Hidden : SequenceState.Dimmed)
                }))
            }));
            return reduceStateFor(this.state, nextDataSets);
        });
        
        // Bring all visible timeseries lines from dimmed state to normal
        this.register(names.NORMALIZE, e => {
            const nextDataSets = this.state.dataSets.map(ds => ({
                ...ds,
                sequences: ds.sequences.map(seq => (seq.state === SequenceState.Normal || seq.state === SequenceState.Hidden) ? seq : { ...seq, state:  SequenceState.Normal })
            }));
            return reduceStateFor(this.state, nextDataSets);
        });
        this.register<Key>(names.TOGGLE_VISIBILITY, e => {
            const { dataSets } = this.state
            const target = e.payload;
            const targetSeq = findSequence(dataSets, target);
            const targetState = (targetSeq.state === SequenceState.Hidden) ? SequenceState.Highlighted : SequenceState.Hidden;

            // All states should be treated as immutable so we have to create deep copy of the dataSets array
            const nextDataSets = this.state.dataSets.map(ds => ({
                ...ds,
                sequences: ds.sequences.map(seq => ({
                        ...seq,
                        state: (seq.id === target)
                            ? targetState
                            : (seq.state === SequenceState.Hidden) ? SequenceState.Hidden : ((targetState === SequenceState.Hidden) ? SequenceState.Normal : SequenceState.Dimmed)
                    }
                ))
            }));
            return reduceStateFor(this.state, nextDataSets);
        });

        // When we use zoom/brush we have to clip time series data to the specified range of interest
        this.register<[Date, Date]>(names.SET_RANGE, e => {
            const range = e.payload;
            const { dataSets } = this.state;
            return this.field({ range, zoomedDataSets: zoomTo(dataSets, range) });
        });
        
        // When we move sweep line, tooltips values are changed, so we have to update it
        this.register<Date>(names.SET_TOOLTIP_TIME, e => {
            const { dataSets } = this.state;
            const timeOfInterest = e.payload;
            const tooltip = timeOfInterest ? getTooltipInfoAt(this.state.dataSets, timeOfInterest) : null;
            return this.field({ tooltip });
        });
    }

    getDataSets = () => {
        return this.state.dataSets;
    }
}

// Creates an empty state for TimeSeriesStore. 
// Empty state means no data available at all
const initialState = (): TimeSeriesState => ({
    dataSets: [],
    zoomedDataSets: [],
    range: null, tooltip: null
});

// This reducer transforms old state with respect to received DataSet
// Basically we need to calculate tooltip's data as well as clip timeseries data to the zoomed time range
const reduceStateFor = (oldState: TimeSeriesState, dataSets: IDataSet[], prepareBaseLineData?: Boolean): TimeSeriesState => {
    const { tooltip, range } = oldState;
    
    let indexOfPsi = dataSets.map((datasets) => { return datasets.yAxisTitle }).indexOf("psi");
    if(prepareBaseLineData && indexOfPsi >= 0){

        //when there's just 1 sensor selected
        if(indexOfPsi < 0 && dataSets[0]){
            indexOfPsi = dataSets.map((datasets) => { return datasets.yAxisTitle }).indexOf(dataSets[0].yAxisTitle);
        }
        const baseLines = dataSets[indexOfPsi].sequences.map(sequences => sequences.BaselineValue); // (window as any).sensors.map((x) => x.Baseline).filter((x) => !!x);
        const uniqBaseLines = _.uniq(baseLines);
        let sequences = dataSets[indexOfPsi].sequences.filter((sequence, idx) => { 
            return !sequence.baseLine;
        });
        uniqBaseLines.map((baseLineValue) => {
            let sequence = Object.assign({}, dataSets[indexOfPsi].sequences[dataSets[indexOfPsi].sequences.length - 1]);
            sequences.push(sequence);
            sequence.id = `Baseline-${baseLineValue}`
            sequence.color = "#000000";
            sequence.title = 'Baseline';
            sequence.state = SequenceState.Normal;
            sequence.baseLine = true;
            sequence.dotted = uniqBaseLines.length > 1;
            sequence.samples = sequence.samples.map((sample) => { return Object.assign({}, sample) });
            sequence.samples = sequence.samples.map((sample) => { sample.y = baseLineValue; return sample });

            dataSets[indexOfPsi].sequences = sequences;
            if(dataSets[indexOfPsi].maxValue < baseLineValue){
                dataSets[indexOfPsi].maxValue = baseLineValue + 5;
            }
            if(dataSets[indexOfPsi].minValue > baseLineValue){
                dataSets[indexOfPsi].minValue = baseLineValue - 5;
            }
        })
    }

    return {
        dataSets,
        range,
        zoomedDataSets: zoomTo(dataSets, range),
        tooltip: tooltip ? getTooltipInfoAt(dataSets, tooltip.x) : null,
    }
}

// Helper method to find timeseries sequence in DataSet container by Key
// It is helpful when we need to togle timeseries visibility by key
const findSequence = (dataSets: IDataSet[], target: Key): IDataSequence => {
    for(let dsIdx = 0; dsIdx < dataSets.length; dsIdx++) {
        const { sequences } = dataSets[dsIdx];
        for(let seqIdx = 0; seqIdx < sequences.length; seqIdx++) {
            const seq = sequences[seqIdx];
            if(seq.id === target) {
                return seq;
            }
        }
    }
}

// Clips provided DataSet to the specified time rage of interest
const zoomTo = (dataSets: IDataSet[], range: [Date, Date]) => {
    return range ? dataSets.map(ds => ({
        ...ds,
        sequences: ds.sequences.map(seq => ({
            ...seq,
            samples: getClip<ISample>(seq.samples, d => d.x, range, sampleInterpolator)
        }))
    })) : dataSets;
}

// Timeseries data is sequence of data points in time
// To render a tooltip we need to interpolate value between two points in a sequence
export const sampleInterpolator = (left: ISample, right: ISample, at: Date): ISample => {
    assertValue(left);
    assertValue(right);
    
    const interpolate = interpolateNumber(left.y, right.y);
    const t = barycentric(left.x, right.x, at);
    return { x: at, y: interpolate(t) };
}

function barycentric(x1: Domain, x2: Domain, x: Domain): number
{
    return ((+x) - (+x1)) / ((+x2) - (+x1));
}

export const getTooltipInfoAt = (dataSets: IDataSet[], at: Date) => ({
    x: at,
    // Each DataSet could have multiple sequences.
    // For each sequence we need to find an intersection point with sweep line (at parameter)
    // Sort constructed array by order flag in a sequence
    items: dataSets.reduce<TooltipItem[]>((acc, ds, idx) => ds.sequences.reduce((acc, seq, sIdx) => {
            acc.push({
                value: getValueAt(seq.samples, at),
                dataSetId: idx,
                sequence: seq,
            });
            return acc;
        }, acc), []).sort((a, b) => a.sequence.order - b.sequence.order)
})

export const getValueAt = (samples: ISample[], at: Date): number => {
    const bisect = bisector<ISample, Date>(d => d.x);
    const idx = bisect.left(samples, at);

    // no values less than x
    if(idx === 0)
        return samples[0].y;
    
    // all values are less
    if (idx === samples.length)
        return samples[samples.length - 1].y;
    
    // samples.slice(0, idx) < x
    return sampleInterpolator(samples[idx - 1], samples[idx], at).y;
}


// WEBPACK FOOTER //
// ./src/charts/TimeSeries/flux/TimeSeriesStore.ts